// Shared logic for the reusable FKE "narrative summary" block (OKV-234). A summary is an AI
// genAnswer fetched from FKE for a set of document revisions; it is rendered inside the
// FkeNarrativeSummary component. Every usage passes its own query, cache key and title, but the
// FKE endpoint, the gating permission and the localStorage cache mechanism are shared here.
import { post as _post } from './fetcher'
import { safeGet } from './safeGet'

// Cache lives in its own dependency-free module (so the auth plugin can clear it without an import
// cycle); re-exported here so callers keep importing everything from utils/fkeSummary.
export { readFkeSummaryCache, writeFkeSummaryCache, clearFkeSummaryCache } from './fkeSummaryCache'

// All summaries are a function of FKE and are gated by the same permission, exposed by the backend
// under userPermissions (the `can` getter reads it verbatim).
export const FKE_SUMMARY_PERMISSION = 'canViewFkeSummary'

const FKE_SUMMARY_COLLECTION = 'okv_documents'
const FKE_SUMMARY_GEN_ANSWER_PROFILE_ID = 'default'

// POST /api/v1/okv/search/fke (auto-scoped to the selected asset class by the fetcher). Resolves the
// genAnswer text. `language` drives genAnswerLanguage (the prompt itself stays as authored).
export function fetchFkeSummary({ query, revisionIds, language }) {
  return _post('/api/v1/okv/search/fke', {
    collection: FKE_SUMMARY_COLLECTION,
    query,
    filters: { revisionId: { in: revisionIds } },
    genAnswer: true,
    genAnswerProfileId: FKE_SUMMARY_GEN_ANSWER_PROFILE_ID,
    genAnswerLanguage: language,
  }).then((resp) => safeGet(() => resp.genAnswer, '') || '')
}

// POST /api/v1/okv/search/fke/documents:filter — a FAST metadata lookup over the same documents the
// genAnswer will read (same revisionIds). Used only to drive the "reasoning" placeholder messages
// while the slow genAnswer runs. Resolves { totalItems, documents: [{ businessType, language,
// issuerName, productName, revisionDate }] }. Shape mirrors the fke-widget's FkeSearchResponse.
export function fetchFkeDocuments({ revisionIds }) {
  return _post('/api/v1/okv/search/fke/documents:filter', {
    collection: FKE_SUMMARY_COLLECTION,
    page: 1,
    pageSize: 20,
    distinctFields: ['sourceDocId', 'productId'],
    filters: { revisionId: { in: revisionIds } },
  }).then((resp) => {
    const items = safeGet(() => resp.result, null) || safeGet(() => resp.data, null) || []
    const documents = items.map((it) => ({
      businessType: safeGet(() => it.businessType, '') || '',
      language: safeGet(() => it.metadata.language, '') || '',
      issuerName: safeGet(() => it.metadata.issuerName, '') || '',
      productName: safeGet(() => it.metadata.productName, '') || '',
      revisionDate: safeGet(() => it.metadata.revisionDate, '') || '',
    }))
    const totalItems = safeGet(() => resp.pagination.totalItems, documents.length)
    return { totalItems, documents }
  })
}
